import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Card, Typography, Alert, message, Progress } from 'antd';
import { AudioOutlined, StopOutlined, ClearOutlined, SaveOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useRealtimePCMRecorder } from '../../hooks/useRealtimePCMRecorder';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';
import { useSnapshot } from 'valtio';
import { RecordingDurationStore } from '@/utils/store/recordingDuration';
import { reportRecordingDuration } from '@/services/recordingDurationService';
import { sendMessageErr } from '@/utils/slsClient';

const { Title, Text } = Typography;

// 使用相对路径，通过vite代理转发到后端
const API_BASE_URL = '';

interface OnlineMeetModalProps {
  visible: boolean;
  onClose: () => void;
  onMeetingEnd: (notes: string, transcription: string) => void;
}

/**
 * 在线会议弹窗组件
 * 包含左侧笔记区域(3/4)和右侧实时转录区域(1/4)
 */
const OnlineMeetModal: React.FC<OnlineMeetModalProps> = ({ visible, onClose, onMeetingEnd }) => {
  const { isAuthenticated, user } = useSupabaseAuth();
  const durationState = useSnapshot(RecordingDurationStore);
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [partialText, setPartialText] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [microphonePermission, setMicrophonePermission] = useState<'granted' | 'denied' | 'pending'>('pending');
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const sessionIdRef = useRef<string>('');

  /**
   * 发送PCM音频数据到后端
   */
  const sendPCMData = async (pcmData: ArrayBuffer) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/speech/audio?sessionId=${sessionIdRef.current}`, {
        method: 'POST',
        body: new Uint8Array(pcmData),
        headers: { 'Content-Type': 'application/octet-stream' }
      });
      
      if (!response.ok) {
        console.error('发送音频数据失败:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('发送音频数据错误:', error);
    }
  };

  /**
   * 录音数据处理回调
   */
  const onData = (pcmData: ArrayBuffer) => {
    if (pcmData && pcmData.byteLength > 0) {
      sendPCMData(pcmData);
    }
  };

  /**
   * 录音停止时的回调
   */
  const onStop = (pcmData: ArrayBuffer) => {
    console.log('录音停止，最终音频数据:', pcmData.byteLength, 'bytes');
    if (pcmData && pcmData.byteLength > 0) {
      sendPCMData(pcmData);
    }
  };

  // 使用自定义 PCM 录音 hook
  const { status: recorderStatus, startRecording: startRec, stopRecording: stopRec } = useRealtimePCMRecorder({
    onData,
    onStop,
    sampleRate: 16000,
    channelCount: 1,
    dataInterval: 1000 // 每秒发送一次数据
  });

  /**
   * 生成会话ID
   */
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * 设置错误并发送到远端日志系统
   * @param errorMessage - 错误消息
   * @param module - 模块名称
   * @param keyMsg - 关键消息标识
   */
  const setErrorWithLog = (errorMessage: string, module: string = 'OnlineMeet', keyMsg: string = 'runtime-error') => {
    setError(errorMessage);
    
    // 发送错误日志到远端
    const uid = user?.id || 'unknown';
    const email = user?.email || 'unknown';
    const errorDetail = {
      message: errorMessage,
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current || 'no-session',
      isRecording,
      microphonePermission
    };
    
    sendMessageErr(uid, email, module, keyMsg, JSON.stringify(errorDetail));
  };

  /**
   * 申请麦克风权限
   */
  const requestMicrophonePermission = async () => {
    try {
      setPermissionError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      // 权限申请成功，立即停止流以释放资源
      stream.getTracks().forEach(track => track.stop());
      
      setMicrophonePermission('granted');
      message.success('麦克风权限已获取');
      
    } catch (error: any) {
      console.error('麦克风权限申请失败:', error);
      setMicrophonePermission('denied');
      setPermissionError('麦克风权限申请失败: ' + error.message + '\n请在浏览器设置中允许麦克风访问权限');
    }
  };

  /**
   * 建立SSE连接
   */
  const connectSSE = async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    
    const sessionId = generateSessionId();
    sessionIdRef.current = sessionId;
    
    const eventSource = new EventSource(`${API_BASE_URL}/api/speech/start?sessionId=${sessionId}`);
    eventSourceRef.current = eventSource;
    
    eventSource.addEventListener('partial', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      setPartialText(data.text || '');
      // 有新的转录结果，清除之前的错误提示
      if (data.text) {
        setError(null);
      }
    });
    
    eventSource.addEventListener('final', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      if (data.text) {
        setFinalText(prev => prev + data.text + ' ');
        setPartialText(''); // 清空部分文本
        // 有新的转录结果，清除之前的错误提示
        setError(null);
      }
    });
    
    eventSource.addEventListener('error', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.error('转录错误:', data.error);
      setErrorWithLog('转录错误: ' + data.error, 'OnlineMeet', 'transcription-error');
    });
    
    eventSource.onerror = (error) => {
      console.error('SSE连接错误:', error);
      setErrorWithLog('实时连接中断，请重试', 'OnlineMeet', 'sse-connection-error');
    };
  };

  /**
   * 开始录音和转录
   */
  const startRecording = async () => {
    try {
      setError(null);
      
      // 检查是否达到时长上限
      if (RecordingDurationStore.checkLimit()) {
        message.error('录音时长已达上限，无法继续录音');
        setErrorWithLog('录音时长已达上限，请联系管理员', 'OnlineMeet', 'duration-limit-reached');
        return;
      }
      
      // 建立SSE连接
      await connectSSE();
      
      // 开始PCM录音
      await startRec();
      setIsRecording(true);
      
      // 开始计时
      RecordingDurationStore.start();
      
    } catch (error: any) {
      console.error('开始录音失败:', error);
      setErrorWithLog('开始录音失败: ' + error.message, 'OnlineMeet', 'start-recording-error');
      setIsRecording(false);
    }
  };

  /**
   * 停止录音和转录
   */
  const stopRecording = async () => {
    try {
      // 停止计时
      RecordingDurationStore.stop();
      
      // 停止PCM录音
      await stopRec();
      
      // 发送停止信号
      if (sessionIdRef.current) {
        await fetch(`${API_BASE_URL}/api/speech/stop?sessionId=${sessionIdRef.current}`, {
          method: 'POST'
        });
      }
      
      // 关闭SSE连接
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      
      setIsRecording(false);
      setIsConnected(false);
      
      // 上报录音时长
      if (user?.id) {
        const duration = RecordingDurationStore.currentDuration;
        await reportRecordingDuration(user.id, duration);
      }
      
    } catch (error: any) {
      console.error('停止录音失败:', error);
      setErrorWithLog('停止录音失败: ' + error.message, 'OnlineMeet', 'stop-recording-error');
      setIsRecording(false);
    }
  };

  /**
   * 清空转录内容
   */
  const clearTranscription = () => {
    setFinalText('');
    setPartialText('');
    setError(null);
  };

  /**
   * 清空笔记内容
   */
  const clearNotes = () => {
    setNotes('');
  };

  /**
   * 结束会议并保存内容
   */
  const handleMeetingEnd = () => {
    if (isRecording) {
      stopRecording();
    }
    onMeetingEnd(notes, finalText);
  };

  /**
   * 关闭弹窗时的清理工作
   */
  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    onClose();
  };

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording();
      }
      // 重置计时器
      RecordingDurationStore.reset();
    };
  }, []);

  // 弹窗打开时申请麦克风权限
  useEffect(() => {
    if (visible && microphonePermission === 'pending') {
      requestMicrophonePermission();
    }
  }, [visible]);

  // 监听时长上限，到达后自动停止录音
  useEffect(() => {
    if (durationState.isLimitReached && isRecording) {
      message.warning('录音时长已达上限，自动停止录音');
      stopRecording();
    }
  }, [durationState.isLimitReached, isRecording]);

  return (
    <Modal
      title="在线会议"
      open={visible}
      onCancel={handleClose}
      width="90vw"
      style={{ top: 20 }}
      styles={{ 
        body: { height: '80vh', padding: 0 },
        mask: { backdropFilter: 'blur(8px)' }
      }}
      footer={[
        <Button key="clear-notes" onClick={clearNotes}>
          清空笔记
        </Button>,
        <Button key="clear-transcription" onClick={clearTranscription}>
          清空转录
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          取消
        </Button>,
        <Button key="save" type="primary" onClick={handleMeetingEnd} icon={<SaveOutlined />}>
          结束会议并保存
        </Button>,
      ]}
    >
      <div className="flex h-full">
        {/* 左侧笔记区域 - 占3/4 */}
        <div className="flex-1 w-3/4 p-4 border-r border-gray-200">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <Title level={4} className="m-0">📝 会议笔记</Title>
            </div>
            <textarea
              className="flex-1 w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="在此记录会议要点、决策事项、行动计划等..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ minHeight: '500px' }}
            />
          </div>
        </div>

        {/* 右侧实时转录区域 - 占1/4 */}
        <div className="w-1/4 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <Title level={4} className="m-0">🎤 实时转录</Title>
          </div>

          {/* 录音控制按钮 */}
          <div className="mb-4 space-y-2">
            {/* 时长显示 */}
            <Card size="small" className="mb-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    <ClockCircleOutlined className="mr-1" />
                    录音时长
                  </span>
                  <span className="text-sm font-semibold">
                    {durationState.getFormattedDuration()}
                  </span>
                </div>
                <Progress 
                  percent={durationState.getUsagePercentage()} 
                  size="small"
                  status={durationState.isLimitReached ? 'exception' : 'active'}
                  showInfo={false}
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>剩余: {Math.floor(durationState.getRemainingDuration() / 60)} 分钟</span>
                  <span>上限: {Math.floor(durationState.maxDuration / 60)} 分钟</span>
                </div>
              </div>
            </Card>

            {microphonePermission === 'denied' && (
              <Alert
                message="麦克风权限被拒绝或未检测到麦克风"
                description={permissionError}
                type="error"
                showIcon
                action={
                  <Button size="small" onClick={requestMicrophonePermission}>
                    重新申请
                  </Button>
                }
              />
            )}

            {microphonePermission === 'granted' && (
              <div className="flex flex-col space-y-2">
                {!isRecording ? (
                  <Button
                    type="primary"
                    icon={<AudioOutlined />}
                    onClick={startRecording}
                    disabled={!isAuthenticated}
                    block
                  >
                    开始录音
                  </Button>
                ) : (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={stopRecording}
                    block
                  >
                    停止录音
                  </Button>
                )}

                <div className="text-center">
                  {isRecording && (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <Text type="secondary">录音中...</Text>
                    </div>
                  )}
                  {isConnected && !isRecording && (
                    <Text type="success">已连接</Text>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 转录内容显示区域 */}
          <div className="flex-1 border border-gray-300 rounded-lg p-3 overflow-y-auto bg-gray-50">
            <div className="space-y-2">
              {finalText && (
                <div className="text-sm text-gray-800 leading-relaxed">
                  {finalText}
                </div>
              )}
              {partialText && (
                <div className="text-sm text-gray-500 italic">
                  {partialText}
                </div>
              )}
              {!finalText && !partialText && (
                <div className="text-center text-gray-400 text-sm">
                  转录内容将在此显示
                </div>
              )}
            </div>
          </div>

          {error && (
            <Alert
              message="错误"
              description={error}
              type="error"
              showIcon
              className="mt-2"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OnlineMeetModal;